import React, { useState } from "react";
import { Globe, Power } from "lucide-react";
import { useRevokeAgentConnection } from "../../../hooks/mutations/useRevokeAgentConnection";
import { AgentConnection } from "../../../types";
import { timeAgo } from "../../../lib/timeAgo";

/** The assistants that can reach this person's JuDI now, each one a Turn off away. */

function scopeLabel(scope: AgentConnection["scope"]): string {
  if (scope === "read") return "Looks only";
  if (scope === "read_write_delete") return "Full control";
  return "Adds and changes";
}

function whenLastUsed(connection: AgentConnection): string {
  if (!connection.last_used_at) return "Not used yet";
  return `Used ${timeAgo(connection.last_used_at)}`;
}

const ConnectedAssistants: React.FC<{ connections: AgentConnection[] }> = ({
  connections,
}) => {
  const revokeConnection = useRevokeAgentConnection();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  return (
    <ul className="border border-warm-border rounded-xl overflow-hidden">
      {connections.map((connection) => (
        <li
          key={connection.id}
          className="flex items-center gap-3 p-4 border-b border-warm-border last:border-b-0"
        >
          <div className="w-9 h-9 rounded-lg bg-warm-bg-elevated flex items-center justify-center flex-shrink-0 text-warm-text-secondary">
            <Globe size={17} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-warm-text-primary truncate">
                {connection.name}
              </p>
              <span className="flex-shrink-0 text-[11px] leading-none px-2 py-1 rounded-full border border-warm-border text-warm-text-secondary">
                {scopeLabel(connection.scope)}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-warm-text-tertiary truncate">
              {whenLastUsed(connection)}
            </p>
          </div>

          {confirmingId === connection.id ? (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  revokeConnection.mutate(connection.id);
                  setConfirmingId(null);
                }}
                className="text-xs px-2.5 py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "var(--color-recording)" }}
              >
                Turn it off
              </button>
              <button
                type="button"
                onClick={() => setConfirmingId(null)}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated transition-colors"
              >
                Keep it
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingId(connection.id)}
              aria-label={`Turn off ${connection.name}`}
              className="flex items-center gap-1.5 text-xs text-warm-text-secondary hover:text-[var(--color-recording)] transition-colors px-2.5 py-1.5 rounded-lg border border-warm-border hover:border-[var(--color-recording)] flex-shrink-0"
            >
              <Power size={13} />
              Turn off
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};

export default ConnectedAssistants;
